import { FilteredCreditApplication } from '@applyonline/shared'
import { DataLayerEvent, ETrackedEvents } from '../types/tracking'
import { useEffect } from 'react'

export const useTrackStepReachedEvent = (
  creditApp: FilteredCreditApplication | null,
  eventName: ETrackedEvents,
): void => {
  useEffect(() => {
    if (creditApp) {
      trackEvent(eventName, creditApp)
    }
  }, [creditApp, eventName])
}

export const trackEvent = (eventName: ETrackedEvents, creditApp: FilteredCreditApplication) => {
  const eventData = buildEventData(eventName, creditApp)

  trackGTagManagerEvent(eventData)
}

export const trackGetStartedEvent = (merchantId: string) => {
  trackGTagManagerEvent({
    event: ETrackedEvents.GetStarted,
    merchant_id: merchantId,
  })
}

// Lightweight funnel step-reached tracking (#67415). Unlike useTrackStepReachedEvent,
// this does not require a credit application, so it can be used on the pre-submit
// intake wizard steps where only the merchant id is available.
export const trackFunnelStepEvent = (eventName: ETrackedEvents, merchantId?: string) => {
  trackGTagManagerEvent({
    event: eventName,
    merchant_id: merchantId,
  })
}

export const useTrackFunnelStepReached = (eventName: ETrackedEvents, merchantId?: string): void => {
  useEffect(() => {
    trackFunnelStepEvent(eventName, merchantId)
    // fire once on mount per step
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

const trackGTagManagerEvent = (eventData: DataLayerEvent) => {
  window.dataLayer.push(eventData)
}

const buildEventData = (eventName: ETrackedEvents, creditApp: FilteredCreditApplication): DataLayerEvent => {
  const eventID = `${eventName}-${creditApp.id ?? ''}`
  const requestedAmount = creditApp.requestedLoanAmount

  const baseEventData: DataLayerEvent = {
    event: eventName,
    form_id: eventID,
    credit_application_id: creditApp.id,
    merchant_id: creditApp.merchantId,
    reference_number: creditApp.referenceNumber,
    loan_info: {
      loan_purpose: creditApp.loanPurposeId,
      loan_duration: undefined,
      loan_interest_rate: undefined,
      loan_amount: requestedAmount,
    },
  }

  if (creditApp.normsDecision) {
    return {
      ...baseEventData,
      loan_info: {
        ...baseEventData.loan_info,
        loan_duration: creditApp.finalDecision.maxTermDuration,
        loan_interest_rate: creditApp.normsDecision.interestRate,
        loan_amount: Math.min(requestedAmount, creditApp.finalDecision.maxAmountFinanced),
      },
    }
  }

  if (creditApp.prequalificationDecision) {
    return {
      ...baseEventData,
      loan_info: {
        ...baseEventData.loan_info,
        loan_interest_rate: creditApp.prequalificationDecision.minInterestRate,
        loan_amount: Math.min(requestedAmount, creditApp.prequalificationDecision.maxLoanAmount),
      },
    }
  }

  return baseEventData
}
